import { AudienceSummary } from '@/lib/api/client'
import { populationsApi, usersApi } from '@/lib/api'
import { OnboardingWizardState } from '@/types/components/onboarding'
import { MAX_RECOMMENDED_AUDIENCES, rankAudiencesByRelevance, selectRecommendedAudiences } from '@/lib/audience-relevance'
import {
  fetchAllReadyAudiences,
  fetchFeaturedTwinPoolForAudiences,
  selectFeaturedTwinsFromBookmarkedAudiences,
  toFeaturedTwinUpdatePayload,
} from '@/lib/homepage-personalization'

/**
 * scoreAudiences — client-side audience matching for the Getting Started Guide.
 *
 * Scores marketplace audiences against the user's industry selections and
 * country selections from the onboarding wizard, then returns them sorted
 * highest-first.
 *
 * Scoring strategy:
 *   - Reuses the shared tier-weighted relevance hierarchy:
 *     direct industry + country (1000) → close parent + country (800) →
 *     broad parent + country (600) → direct industry (500) →
 *     close parent (400) → broad parent (300) → country + type (200) →
 *     type (100) → fallback (0).
 *
 * Fallback: if industry/country are empty, audiences are still ranked by
 * B2B/B2C when targetingType is B2B or B2C; otherwise they keep stable order.
 *
 * Cache scope: computed per Getting Started Guide render (once per session).
 */
export interface ScoredAudience {
  audience: AudienceSummary
  score: number
}

export function scoreAudiences(
  audiences: Array<AudienceSummary>,
  wizardState: Pick<OnboardingWizardState, 'selectedIndustries' | 'selectedIndustryAncestors' | 'selectedCountries' | 'targetingType'>,
): Array<ScoredAudience> {
  return rankAudiencesForWizard(audiences, wizardState).map(({ audience, score }) => ({ audience, score }))
}

function rankAudiencesForWizard(
  audiences: Array<AudienceSummary>,
  wizardState: Pick<OnboardingWizardState, 'selectedIndustries' | 'selectedIndustryAncestors' | 'selectedCountries' | 'targetingType'>,
) {
  return rankAudiencesByRelevance(audiences, {
    targetingType: wizardState.targetingType,
    targetingPreferences: {
      industryTokens: wizardState.selectedIndustries,
      industryAncestorSourceIds: wizardState.selectedIndustryAncestors,
      focusMarkets: wizardState.selectedCountries,
    },
  })
}

/**
 * Returns marketplace audiences selected by the shared recommendation policy.
 */
export function getTopAudiences(
  audiences: Array<AudienceSummary>,
  wizardState: Pick<OnboardingWizardState, 'selectedIndustries' | 'selectedIndustryAncestors' | 'selectedCountries' | 'targetingType'>,
  limit: number,
): Array<AudienceSummary> {
  const marketplaceOnly = audiences.filter(
    (a) => a.sharingMode === 'marketplace' && !a.canEdit,
  )
  const ranked = rankAudiencesForWizard(marketplaceOnly, wizardState)
  // Shared recommendations cap at 5; this slice lets preview surfaces request fewer cards.
  return selectRecommendedAudiences(ranked, {
    hasIndustryTargeting: hasWizardIndustryTargeting(wizardState),
  }).slice(0, limit).map((entry) => entry.audience)
}

/**
 * Fetches all ready marketplace audiences, scores them against the user's
 * onboarding selections, and bookmarks the best-fit ones.
 *
 * Uses the shared recommendation policy: top primary matches first, capped at
 * five, with lower-scored positive backfill only when fewer than three primary
 * matches exist.
 *
 * Already-bookmarked private/shared (own or org-visible) audiences count toward
 * the target cap of MAX_RECOMMENDED_AUDIENCES so that the total bookmarked set
 * never exceeds the cap.
 *
 * Called once at onboarding completion so that the audiences lobby is already
 * personalized when the user arrives.
 *
 * Returns the successfully bookmarked audiences so the initial featured-twin
 * seed can use the exact same audience IDs without racing bookmark reads.
 */
export async function bookmarkTopAudiences(
  wizardState: Pick<OnboardingWizardState, 'selectedIndustries' | 'selectedIndustryAncestors' | 'selectedCountries' | 'targetingType'>,
  canAccessMarketplace = true,
): Promise<Array<AudienceSummary>> {
  if (!canAccessMarketplace) return []

  const hasSelections =
    wizardState.selectedIndustries.length > 0 ||
    wizardState.selectedIndustryAncestors.length > 0 ||
    wizardState.selectedCountries.length > 0
  if (!hasSelections) return []

  try {
    const all = await fetchAllReadyAudiences()

    // Count already-bookmarked own/org-shared audiences toward the total cap.
    const alreadyBookmarkedNonMarketplace = all.filter(
      (a) =>
        a.isBookmarked &&
        a.sharingMode !== 'marketplace' &&
        (a.canEdit === true || a.sharingMode === 'organization' || a.sharingMode === 'shared'),
    ).length
    const availableSlots = Math.max(0, MAX_RECOMMENDED_AUDIENCES - alreadyBookmarkedNonMarketplace)
    if (availableSlots === 0) return []

    const readyMarketplace = all.filter(
      (a) => a.sharingMode === 'marketplace' && !a.canEdit,
    )
    const ranked = rankAudiencesForWizard(readyMarketplace, wizardState)
    const toBookmark = selectRecommendedAudiences(ranked, {
      hasIndustryTargeting: hasWizardIndustryTargeting(wizardState),
    }).slice(0, availableSlots)

    const bookmarkedAt = new Date().toISOString()
    const results = await Promise.allSettled(
      toBookmark.map(async (entry) => {
        if (!entry.audience.isBookmarked) {
          await populationsApi.bookmark(entry.audience.id)
        }
        return {
          ...entry.audience,
          isBookmarked: true,
          bookmarkedAt: entry.audience.bookmarkedAt ?? bookmarkedAt,
        }
      }),
    )
    return results.flatMap((result) =>
      result.status === 'fulfilled' ? [result.value] : [],
    )
  } catch {
    // Intentionally silent — never block onboarding completion
    return []
  }
}

export async function seedInitialFeaturedTwinsFromAudiences(
  bookmarkedAudiences: Array<AudienceSummary>,
): Promise<void> {
  if (bookmarkedAudiences.length === 0) return

  try {
    // Skip if the user already has featured twins — avoid overwriting existing personalization.
    const existingTwins = await populationsApi.getAllFeaturedTwins()
    if (existingTwins.length > 0) return

    const marketplaceTwins = await fetchFeaturedTwinPoolForAudiences(bookmarkedAudiences)
    const selected = selectFeaturedTwinsFromBookmarkedAudiences({
      bookmarkedAudiences,
      marketplaceTwins,
    })
    if (selected.length === 0) return
    await usersApi.updateFeaturedTwins(selected.map(toFeaturedTwinUpdatePayload))
  } catch {
    // Intentionally silent — initial twin seeding should not block onboarding.
  }
}

function hasWizardIndustryTargeting(
  wizardState: Pick<OnboardingWizardState, 'selectedIndustries' | 'selectedIndustryAncestors'>,
): boolean {
  return (
    wizardState.selectedIndustries.length > 0 ||
    wizardState.selectedIndustryAncestors.length > 0
  )
}
